import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useProductCodes } from '../context/ProductCodesContext';

/**
 * Drop-in wrapper that prefixes a product name with its code when an admin
 * is viewing. Non-admins see only the product name (codes hidden).
 *
 * Usage:
 *   <CodeLabel name={b.product_name} />
 *   <CodeLabel name={b.product_name} inline className="text-xs" />
 *   <CodeLabel name={b.product_name} codeClass="text-amber-400" />
 *
 * Props:
 *   - name: string (required)
 *   - inline: boolean (render as single line)
 *   - className, codeClass, nameClass: style overrides
 *   - showCodeOnlyIfMissingName: boolean (rare — show just the code when name is empty)
 */
export default function CodeLabel({
  name,
  inline = true,
  className = '',
  codeClass = 'font-mono font-semibold text-amber-400',
  nameClass = '',
  fallback = '—',
  showCodeOnlyIfMissingName = false,
}) {
  const { user } = useAuth();
  const { lookup } = useProductCodes();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const code = lookup(name);

  if (!name && !showCodeOnlyIfMissingName) {
    return <span className={className}>{fallback}</span>;
  }

  const Wrap = inline ? 'span' : 'div';

  return (
    <Wrap className={className} data-testid="code-label">
      {isAdmin && code && (
        <span className={`${codeClass} mr-1.5`} data-testid="code-label-code">{code}</span>
      )}
      <span className={nameClass}>{name || ''}</span>
    </Wrap>
  );
}
